import type { GridApi, GridGroupNode, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type { ItemType } from '@/common/api/types/itemType';
import { useItemPanelNavigation } from '@/features/item-panel/hooks/useItemPanelParams';
import {
  ItemNameCellContent,
  MissingItemWarningIcon,
} from '@/features/items/components/data-table/cells/ItemNameCell';
import type { ItemsTableRow } from '../types';

export const ItemNameCell = ({
  value,
  row,
  rowNode,
  itemType,
  field,
  api,
  tabIndex,
}: GridRenderCellParams<ItemsTableRow, string, string, GridGroupNode> & {
  itemType?: ItemType;
}) => {
  const { openItemPanel } = useItemPanelNavigation();
  const itemId = row.id;
  const gridApi = api as GridApi;
  const itemName = value ?? '';

  const isExpanded = Boolean(rowNode.childrenExpanded);
  const isMissingItem = row.isDeletedInItemStore ?? false;
  const showDescendantMissingWarning =
    !isMissingItem && (row.hasDeletedDescendant ?? false) && !isExpanded;
  const showMissingItemIcon = isMissingItem || showDescendantMissingWarning;

  return (
    <ItemNameCellContent
      itemName={itemName}
      itemType={itemType}
      externalKey={row.externalKey}
      rowDepth={rowNode.depth}
      canExpand={Boolean(rowNode.children?.length)}
      isExpanded={isExpanded}
      toggleExpanded={() => {
        gridApi.setRowChildrenExpansion(itemId, !rowNode.childrenExpanded);
        gridApi.setCellFocus(itemId, field);
      }}
      onItemNameClick={() => openItemPanel({ itemId })}
      disableNameClick={isMissingItem}
      tabIndex={tabIndex}
      additionalIcons={
        showMissingItemIcon && (
          <MissingItemWarningIcon isDescendantWarning={showDescendantMissingWarning} />
        )
      }
      className="w-full"
      data-testid="item-name-cell"
    />
  );
};
